/**
 * @file Data-driven prop catalog for the dungeon generator (issue #44).
 *
 * This table is the single source of truth for *which* props exist, *where*
 * they are placed, and *how* they collide. The generator's decoration step
 * ({@link ../world/dungeon/generateDungeon.js} step 7) iterates this catalog
 * instead of chaining hand-written `if`s, and {@link ../world/dungeon/DungeonArena.js}
 * reads each row's `collider` to decide which props become real blockers.
 *
 * Adding a prop kind, retuning its density, or giving it collision is a new/edited
 * row here — never a code change in the generator (GDD: "content is data").
 *
 * ## Placement strategies
 * - `roomCenter`   — one instance at the room's centre cell.
 * - `grid`         — evenly stepped interior grid (columns, gravestones).
 * - `ring`         — N instances on a circle around the room centre.
 * - `scatterRoom`  — random interior cells inside a specific room type.
 * - `globalScatter`— random floor cells across the whole map (ambient debris).
 * - `themeSweep`   — placement is owned by the theme-atmosphere pass in the
 *                    generator (wall-adjacency / pool-edge geometry). The row is
 *                    still registered here so the catalog stays the complete
 *                    registry and the collider/density knobs live in data.
 *
 * ## Collider
 * `null` for flat/decorative props (never a blocker). Otherwise
 * `{ halfX, halfZ, blocksProjectiles }` in **world units** (post tile scaling is
 * applied by DungeonArena). `blocksProjectiles: true` extends the collider mask
 * to bullet layers so the prop is real cover; `false` blocks only bodies.
 */

/**
 * @typedef {object} PropCollider
 * @property {number} halfX Half-extent along X, world units.
 * @property {number} halfZ Half-extent along Z, world units.
 * @property {boolean} blocksProjectiles Whether bullets are stopped too.
 */

/**
 * @typedef {object} PropRow
 * @property {string} kind Instance-set key shared with DungeonBuilder.
 * @property {string[]|'*'} roomTypes Room types this prop may appear in.
 * @property {string[]|'*'} themes Theme keys allowed, or '*'.
 * @property {string} [roomFlag] Only place when the room carries this flag (e.g. 'grave').
 * @property {string} placement One of the strategies documented above.
 * @property {PropCollider|null} collider Collision policy, or null for decoration.
 * @property {object} [params] Strategy-specific tuning.
 */


/**
 * @typedef {object} DestructibleSpec
 * @property {number} maxHealth Hit points before it breaks.
 * @property {string[]} destroyedBy Damage types that may break it
 *   (`'explosion' | 'bullet' | 'melee'`). Anything else is shrugged off.
 * @property {number} debrisCount Debris particles emitted on death.
 * @property {boolean} leavesRubble Whether a flat, non-solid rubble decal remains.
 * @property {string | null} loot Powerup key to drop on death, or null.
 */

/**
 * @typedef {object} ExplosiveSpec
 * @property {number} radius Blast radius in world units.
 * @property {number} damage Centre damage; falls off linearly to the rim.
 * @property {boolean} affectsEnvironment Whether the blast can break other
 *   destructibles (and so start a chain reaction).
 */

/** @type {Readonly<Record<string, PropRow>>} */
export const DUNGEON_PROPS = Object.freeze({
  /* ---- structured, table-driven placement ---- */

  ring: {
    kind: 'ring',
    roomTypes: ['entrance'],
    themes: '*',
    placement: 'roomCenter',
    collider: null,
    params: { rotation: 'fixed' }
  },

  bossCrystal: {
    kind: 'bossCrystal',
    roomTypes: ['boss'],
    themes: '*',
    placement: 'roomCenter',
    // Sits on the exit tile; no collider so it never blocks the level-complete pad.
    collider: null,
    params: { rotation: 'free' }
  },

  brazier: {
    kind: 'brazier',
    roomTypes: ['boss'],
    themes: '*',
    placement: 'ring',
    collider: { halfX: 0.4, halfZ: 0.4, blocksProjectiles: false },
    params: { count: 6, radiusInset: 2, minRadius: 2.5, avoidDoorways: 1, rotation: 'fixed' }
  },

  chest: {
    kind: 'chest',
    roomTypes: ['treasure'],
    themes: '*',
    placement: 'roomCenter',
    collider: { halfX: 0.5, halfZ: 0.5, blocksProjectiles: false },
    params: { rotation: 'quarterTurns' }
  },

  shrineCrystal: {
    kind: 'shrineCrystal',
    roomTypes: ['shrine'],
    themes: '*',
    placement: 'roomCenter',
    collider: { halfX: 0.5, halfZ: 0.5, blocksProjectiles: false },
    params: { rotation: 'free' }
  },

  pillar: {
    kind: 'pillar',
    roomTypes: ['combat', 'elite'],
    themes: '*',
    placement: 'grid',
    // The headline feature: real, projectile-blocking cover for combat rooms.
    collider: { halfX: 0.58, halfZ: 0.58, blocksProjectiles: true },
    params: {
      minRoomSize: 10,
      stepSmall: 3,
      stepLarge: 4,
      largeThreshold: 14,
      avoidDoorways: 2,
      requiresInterior: true,
      skipCenter: true,
      forbidShapes: ['ellipse'],
      forbidRoomFlags: ['grave', 'lake'],
      scale: { min: 0.94, max: 1.06 },
      rotation: 'fixed'
    }
  },

  sarco: {
    kind: 'sarco',
    roomTypes: '*',
    themes: '*',
    roomFlag: 'grave',
    placement: 'roomCenter',
    collider: { halfX: 0.78, halfZ: 0.46, blocksProjectiles: true },
    params: { minRoomSize: 10, rotation: 'halfTurns' }
  },

  grave: {
    kind: 'grave',
    roomTypes: '*',
    themes: '*',
    roomFlag: 'grave',
    placement: 'grid',
    collider: { halfX: 0.4, halfZ: 0.34, blocksProjectiles: false },
    params: {
      step: 2,
      avoidDoorways: 2,
      requiresInterior: true,
      skipCenterRadius: 1,
      chance: 0.8,
      scale: { min: 0.85, max: 1.15 },
      rotation: 'jitter'
    }
  },

  candle: {
    kind: 'candle',
    roomTypes: '*',
    themes: '*',
    roomFlag: 'grave',
    placement: 'scatterRoom',
    collider: null,
    params: { count: 4, scale: { min: 0.85, max: 1.2 }, rotation: 'fixed' }
  },

  debris: {
    kind: 'debris',
    roomTypes: '*',
    themes: '*',
    placement: 'globalScatter',
    collider: null,
    params: { baseDensity: 0.045, corridorFactor: 0.45, variants: 3 }
  },

  /* ---- space-station props (feature/mapas) ---- */

  /** Stacked cargo containers: hangar cover the player can hide behind. */
  cargo: {
    kind: 'cargo',
    roomTypes: ['combat', 'elite', 'treasure'],
    themes: ['hangar'],
    placement: 'scatterRoom',
    collider: { halfX: 0.44, halfZ: 0.44, blocksProjectiles: true },
    params: { count: 3, scale: { min: 0.9, max: 1.35 }, rotation: 'jitter' }
  },

  /** Rows of med beds with a small vitals screen. Bodies walk around them. */
  medBed: {
    kind: 'medBed',
    roomTypes: ['combat', 'elite'],
    themes: ['medbay'],
    placement: 'grid',
    collider: { halfX: 0.32, halfZ: 0.52, blocksProjectiles: false },
    params: {
      step: 3,
      avoidDoorways: 2,
      requiresInterior: true,
      skipCenterRadius: 1,
      chance: 0.7,
      rotation: 'fixed'
    }
  },

  /** Specimen tanks ringing lab rooms, glowing core inside a glass shell. */
  labTank: {
    kind: 'labTank',
    roomTypes: ['combat', 'elite', 'shrine'],
    themes: ['lab'],
    placement: 'ring',
    collider: { halfX: 0.34, halfZ: 0.34, blocksProjectiles: false },
    params: {
      count: 5,
      minRadius: 2.5,
      radiusInset: 2,
      avoidDoorways: 2,
      rotation: 'fixed',
      scale: { min: 0.9, max: 1.1 }
    }
  },

  /** Long grow trays with an emissive crop strip. Low cover, walkable lanes. */
  planter: {
    kind: 'planter',
    roomTypes: ['combat', 'elite', 'treasure'],
    themes: ['hydroponics'],
    placement: 'grid',
    collider: { halfX: 0.7, halfZ: 0.26, blocksProjectiles: false },
    params: {
      step: 3,
      avoidDoorways: 2,
      requiresInterior: true,
      skipCenterRadius: 1,
      chance: 0.75,
      rotation: 'fixed'
    }
  },

  /** A control console with a lit screen — ambient tech dressing everywhere. */
  console: {
    kind: 'console',
    roomTypes: ['combat', 'elite'],
    themes: ['hangar', 'medbay', 'lab', 'derelict', 'reactor'],
    placement: 'scatterRoom',
    collider: { halfX: 0.32, halfZ: 0.24, blocksProjectiles: false },
    params: { count: 1, rotation: 'jitter', scale: { min: 0.9, max: 1.1 } }
  },

  /* ---- central blocks (generator pass 7.0): per-cell colliders + one visual ---- */

  /** Invisible per-cell collider under a central block. Full-tile, solid cover. */
  hullBlock: {
    kind: 'hullBlock',
    roomTypes: '*',
    themes: '*',
    placement: 'themeSweep',
    collider: { halfX: 1, halfZ: 1, blocksProjectiles: true }
  },
  /** Docked shuttle parked in the middle of a hangar bay. Purely visual. */
  ship: { kind: 'ship', roomTypes: '*', themes: ['hangar'], placement: 'themeSweep', collider: null },
  /** Central service core: machinery island the room circulates around. */
  serviceCore: { kind: 'serviceCore', roomTypes: '*', themes: '*', placement: 'themeSweep', collider: null },

  /* ---- theme-atmosphere sweeps (placement owned by the generator's theme pass) ----
     Registered here so the catalog is the complete registry and their collider
     policy is data. None collide: they are flat decals or wall-mounted dressing. */

  bones: { kind: 'bones', roomTypes: '*', themes: ['quarantine'], placement: 'themeSweep', collider: null },
  moss: { kind: 'moss', roomTypes: '*', themes: ['hydroponics'], placement: 'themeSweep', collider: null },
  roots: { kind: 'roots', roomTypes: '*', themes: ['hydroponics'], placement: 'themeSweep', collider: null },
  icicle: { kind: 'icicle', roomTypes: '*', themes: ['cryo'], placement: 'themeSweep', collider: null },
  shardIce: { kind: 'shardIce', roomTypes: '*', themes: ['cryo'], placement: 'themeSweep', collider: null },
  crack: { kind: 'crack', roomTypes: '*', themes: '*', placement: 'themeSweep', collider: null },
  banner: { kind: 'banner', roomTypes: ['elite', 'boss'], themes: '*', placement: 'themeSweep', collider: null },
/* ---- destructible entities (issue #47): spawned individually by the ----
     ---- arena's destructible pass, never by the placement strategies  ---- */

  /** Breakable wooden crate. One hit from any accepted damage type breaks it. */
  crate: {
    kind: 'crate',
    collider: { halfX: 0.5, halfZ: 0.5, blocksProjectiles: true },
    visualScale: 1,
    destructible: {
      maxHealth: 1,
      destroyedBy: ['explosion', 'bullet', 'melee'],
      debrisCount: 8,
      leavesRubble: true,
      loot: null
    }
  },

  /** Explosive barrel. One hit sets it off, then it can chain into its neighbours. */
  barrel: {
    kind: 'barrel',
    collider: { halfX: 0.5, halfZ: 0.5, blocksProjectiles: true },
    visualScale: 1,
    destructible: {
      maxHealth: 1,
      destroyedBy: ['explosion', 'bullet', 'melee'],
      debrisCount: 12,
      leavesRubble: true,
      loot: null
    },
    explosive: {
      radius: 4.5,
      damage: 55,
      affectsEnvironment: true
    }
  },

  /**
   * Reinforced crate: an indestructible worked example. With no `destructible`
   * block it never gains a Destructible component — a solid, permanent obstacle.
   */
  crateReinforced: {
    kind: 'crate',
    collider: { halfX: 0.5, halfZ: 0.5, blocksProjectiles: true },
    visualScale: 1.15
  }
});

/**
 * Rows the generic table-driven placer handles (everything except theme sweeps),
 * in a stable placement order so results stay deterministic per seed.
 * @type {ReadonlyArray<PropRow>}
 */
export const PLACEABLE_PROPS = Object.freeze(
  Object.values(DUNGEON_PROPS).filter((row) => row.placement && row.placement !== 'themeSweep')
);

/**
 * Whether a prop kind is a solid blocker (has a collider).
 * @param {string} kind Prop kind.
 * @returns {boolean}
 */
export function propBlocks(kind) {
  return Boolean(DUNGEON_PROPS[kind]?.collider);
}

/**
 * Collider config for a prop kind, or null.
 * @param {string} kind Prop kind.
 * @returns {PropCollider|null}
 */
export function propCollider(kind) {
  return DUNGEON_PROPS[kind]?.collider ?? null;
}

/**
 * @param {string} key A {@link DUNGEON_PROPS} key.
 * @returns {boolean} Whether that prop can be destroyed.
 */
export function isDestructible(key) {
  return Boolean(DUNGEON_PROPS[key]?.destructible);
}
